import { useSelector } from "react-redux";
import network from "../../../selectors/network";
import { useMutation, useQuery } from "react-query";
import {
  BackgroundJob,
  BackgroundJobResults,
  cancelJob,
  getBackgroundJob,
  getJobResultsSync,
} from "../queries/backgroundJobs";
import { useState } from "react";
import { useToasts } from "react-toast-notifications";
import extractErrorInfo from "../../../utils/extractErrorInfo";

const isJobFinished = (status?: string) => {
  return status === "Cancelled" || status === "Completed" || status === "Errored";
};

interface IUseBackgroundJobOptions {
  refetchInterval?: number | false;
  getJobResultsWhenDone?: boolean;
  onJobResultsFinished?: (job: BackgroundJob, results: BackgroundJobResults) => void;
  toastOnError?: boolean;
}

export function useBackgroundJob(
  id: number,
  {
    refetchInterval: defaultRefetchInterval,
    getJobResultsWhenDone: defaultGetJobResultsWhenDone = false,
    onJobResultsFinished,
    toastOnError = true,
  }: IUseBackgroundJobOptions = {},
) {
  const networkHeaders = useSelector(network.defaultHeaders);
  const { addToast } = useToasts();
  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(defaultRefetchInterval);
  const [getJobResultsWhenDone, setGetJobResultsWhenDone] = useState(defaultGetJobResultsWhenDone);
  const [backgroundJobError, setBackgroundJobError] = useState<{
    title: string;
    subHeader: string;
    message: string;
  } | null>(null);
  const {
    data: backgroundJob,
    isLoading: isBackgroundJobLoading,
    isError: isBackgroundJobErrored,
  } = useQuery(["BackgroundJob", id], {
    queryFn: getBackgroundJob(networkHeaders),
    queryFnParamsFilter: ([, id]) => {
      return [id];
    },
    refetchInterval,
    // current version of useQuery does not allow a function for refetchInterval
    onSuccess: (data) => {
      if (isJobFinished(data.status)) {
        setRefetchInterval(false); // Stop polling when job is complete
      }
    },
    onError: (error) => {
      const { title, subHeader, message } = extractErrorInfo(error);
      setBackgroundJobError({ title, subHeader, message });
      const errorMessage = `${title} ${subHeader} ${message}`;
      if (toastOnError) {
        addToast(errorMessage, {
          appearance: "error",
          autoDismiss: true,
        });
      }
      setRefetchInterval(false); // Stop polling on error
    },
  });

  const [isCancelJobMutationLoading, setIsCancelJobMutationLoading] = useState(false);
  const [cancelJobMutation] = useMutation(
    () => {
      setIsCancelJobMutationLoading(true);
      return cancelJob(networkHeaders)(id);
    },
    {
      onError: (error) => {
        const { title, subHeader, message } = extractErrorInfo(error);
        const errorMessage = `${title} ${subHeader} ${message}`;
        if (toastOnError) {
          addToast(errorMessage, {
            appearance: "error",
            autoDismiss: true,
          });
        }
      },
      onSettled: () => {
        setIsCancelJobMutationLoading(false);
      },
    },
  );

  const [backgroundJobResultsError, setBackgroundJobResultsError] = useState<{
    title: string;
    subHeader: string;
    message: string;
  } | null>(null);
  const {
    data: backgroundJobResults,
    isLoading: isBackgroundJobResultsLoading,
    isError: isBackgroundJobResultsErrored,
  } = useQuery(["BackgroundJobResults", id], {
    queryFn: getJobResultsSync(networkHeaders),
    queryFnParamsFilter: ([, id]) => {
      return [id];
    },
    enabled: getJobResultsWhenDone && isJobFinished(backgroundJob?.status),
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (backgroundJob) {
        onJobResultsFinished?.(backgroundJob, data);
      }
    },
    onError: (error) => {
      const { title, subHeader, message } = extractErrorInfo(error);
      setBackgroundJobResultsError({ title, subHeader, message });
      const errorMessage = `${title} ${subHeader} ${message}`;
      if (toastOnError) {
        addToast(errorMessage, {
          appearance: "error",
          autoDismiss: true,
        });
      }
    },
  });

  const enableFetchJobResults = () => {
    setGetJobResultsWhenDone(true);
  };

  return {
    backgroundJob,
    backgroundJobError,
    isBackgroundJobLoading,
    isBackgroundJobErrored,
    isCancelJobMutationLoading,
    cancelJobMutation,
    backgroundJobResults,
    backgroundJobResultsError,
    isBackgroundJobResultsLoading,
    isBackgroundJobResultsErrored,
    enableFetchJobResults,
  };
}
